import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {
    createFolder, createNormativeDocument,
    deleteElement,
    getFolderNodes,
    moveFolderTreeElement,
    selectFolderNodes,
    selectFolders,
    selectLoading,
    selectNormativeDocuments,
    updateFolder,
    uploadExcelNormativeDocument
} from "../../redux/reducers/normative-documents-reducer.js";
import {CommonLayout} from "../../layouts/CommonLayout/CommonLayout";
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import Typography from "@material-ui/core/Typography";
import {makeStyles} from "@material-ui/styles";
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import {Menu, MenuItem, Paper} from "@material-ui/core";
import DocumentsTreeView from "../../components/DocumentsTreeView/DocumentsTreeView";
import DeleteDialog from "../../components/DeleteDialog/DeleteDialog.jsx";
import AddFolderDialog from "../../components/DocumentsTreeDialogs/AddFolderDialog.jsx";
import {useDialog} from "../../hooks/useDialog.js";
import AddDocumentDialog from "../../components/DocumentsTreeDialogs/AddDocumentDialog.jsx";
import {RouteNames} from "../../router/consts.js";
import {createInfoNotification} from "../../redux/reducers/notification-reducer.js";
import EditFolderDialog from "../../components/DocumentsTreeDialogs/EditFolderDialog.jsx";
import {useRedirect} from "../../hooks/useRedirect";

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(1),
        width: '100%',
    },
    header: {
        paddingBottom: theme.spacing(1)
    },
}))

const AddMenu = (props) => {
    const {anchorEl, handleClose, title, onAddFolderButtonClick, onAddDocumentButtonClick} = props

    const handleAddFolderButtonClick = () => {
        handleClose()
        onAddFolderButtonClick()
    }

    const handleAddDocumentButtonClick = () => {
        handleClose()
        onAddDocumentButtonClick()
    }

    return (
        <Menu
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
        >
            <MenuItem onClick={handleAddFolderButtonClick}>Добавить папку в {title}</MenuItem>
            <MenuItem onClick={handleAddDocumentButtonClick}>Добавить документ в {title}</MenuItem>
        </Menu>
    );
}

const NormativeDocumentsTreePage = () => {
    const classes = useStyles()

    const dispatch = useDispatch()

    const {redirect} = useRedirect()

    const folderNodes = useSelector(selectFolderNodes)
    const folders = useSelector(selectFolders)
    const normativeDocuments = useSelector(selectNormativeDocuments)
    const loading = useSelector(selectLoading)

    const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null)
    const [selectedNode, setSelectedNode] = useState('')
    const [parentFolderID, setParentFolderID] = useState(null)
    const [cutNode, setCutNode] = useState('')
    const [editFolderID, setEditFolderID] = useState(null)

    const addFolderDialog = useDialog()
    const addDocumentDialog = useDialog()
    const editFolderDialog = useDialog()
    const deleteDialog = useDialog()

    const typeIcons = {
        folder: FolderOpenIcon,
        normativeDocument: DescriptionOutlinedIcon
    }

    const iconTooltips = {
        folder: 'Папка',
        normativeDocument: 'Документ'
    }

    const getParentNode = (selectedNode) => {
        const [type, id] = selectedNode.split('.')
        if (id === undefined) return null
        if (type === 'normativeDocument') {
            return normativeDocuments.find(doc => doc.id === parseInt(id)).folder
        }
        if (type === 'folder') {
            return folders.find(folder => folder.id === parseInt(id)).id
        }
    }

    const handleCloseAddMenu = () => {
        setAddMenuAnchorEl(null)
    }

    const toolbarCallbacks = {
        onAdd: (nodeID) => async (event) => {
            await setParentFolderID(getParentNode(nodeID))
            await setAddMenuAnchorEl(event.target)
        },
        onEdit: (nodeID) => async () => {
            const [type, id] = nodeID.split('.')
            if (type === 'normativeDocument') {
                redirect(RouteNames.NORMATIVE_DOCUMENT_EDIT, {id})
            }
            if (type === 'folder') {
                await setEditFolderID(id)
                editFolderDialog.handleOpen()
            }
        },
        onDelete: (nodeID) => async () => {
            await setSelectedNode(nodeID)
            await deleteDialog.handleOpen()
        },
        onCut: (nodeID) => async () => {
            await setCutNode(nodeID)
            dispatch(createInfoNotification('Вырезано'))
        },
        onPaste: (nodeID) => async () => {
            const result = await dispatch(moveFolderTreeElement(cutNode, getParentNode(nodeID)))
            if (result === null) return result
            await setCutNode('')
            await dispatch(getFolderNodes())
        },
        onDoubleClick: (nodeID) => async () => {
            const [type, id] = nodeID.split('.')
            if (type === 'normativeDocument') {
                redirect(RouteNames.NORMATIVE_DOCUMENT_EDIT, {id})
            }
        }
    }

    useEffect(() => {
        dispatch(getFolderNodes())
    }, [])

    return (
        <CommonLayout>
            <AddMenu
                anchorEl={addMenuAnchorEl}
                handleClose={handleCloseAddMenu}
                title={parentFolderID === null ? 'корневую папку' : folders.find(folder => folder.id === parentFolderID)?.name}
                onAddFolderButtonClick={addFolderDialog.handleOpen}
                onAddDocumentButtonClick={addDocumentDialog.handleOpen}
            />
            <AddFolderDialog
                open={addFolderDialog.open}
                onClose={addFolderDialog.handleClose}
                submitCallback={async (values) => {
                    await dispatch(createFolder({...values, folder: parentFolderID}))
                    await dispatch(getFolderNodes())
                }}
            />
            <EditFolderDialog
                open={editFolderDialog.open}
                onClose={editFolderDialog.handleClose}
                initialValues={folders.find(folder => folder.id === parseInt(editFolderID))}
                submitCallback={async (values) => {
                    await dispatch(updateFolder(values))
                    await dispatch(getFolderNodes())
                }}
            />
            <AddDocumentDialog
                open={addDocumentDialog.open}
                onClose={addDocumentDialog.handleClose}
                uploadFileCallback={async (file, onSuccess) => {
                    await dispatch(uploadExcelNormativeDocument(file, parentFolderID, onSuccess))
                    await dispatch(getFolderNodes())
                }}
                createEmptyDocumentCallback={async (onSuccess) => {
                    await dispatch(createNormativeDocument(parentFolderID, onSuccess))
                    await dispatch(getFolderNodes())
                }}
                loading={loading}
            />

            <DeleteDialog
                open={deleteDialog.open}
                onClose={deleteDialog.handleClose}
                loading={loading}
                submitCallback={async () => {
                    await dispatch(deleteElement(selectedNode))
                    await dispatch(getFolderNodes())
                }}
            />

            <Typography className={classes.header} variant={'h5'}>Нормативные документы</Typography>
            <Paper className={classes.paper}>
                <DocumentsTreeView
                    nodes={folderNodes}
                    typeIcons={typeIcons}
                    isAbleToPaste={Boolean(cutNode)}
                    iconTooltips={iconTooltips}
                    toolbarCallbacks={toolbarCallbacks}
                />
            </Paper>
        </CommonLayout>
    );
};

export default NormativeDocumentsTreePage;